import { Alert, Box, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import logoConstructora from '../../../assets/img/logo_constructora.png';
import {
  DrawerFormPreview,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common/components';
import {
  buildPaymentOrderGroups,
  createEmptyPaymentForm,
  getPaymentOrderStatusLabel,
} from '../utils/supplierLedgerHelpers';
import { formatAmount, formatCurrencyAmount, parseNumber } from '../utils/supplierLedgerFormatters';
import { AmountPanel, InvoicePreviewField } from './SupplierLedgerShared';

const PaymentOrderPreview = ({
  group,
  applications,
  form,
  paymentForms,
  status,
  includedInvoices,
  providerMap,
}) => {
  const included = group?.invoices.filter((invoice) => includedInvoices[invoice.uuid]) || [];
  const internalOrders = buildPaymentOrderGroups(included, providerMap);
  const paymentForm = paymentForms?.[group?.providerId] || createEmptyPaymentForm(form.emission_date);

  if (!group || !included.length) {
    return (
      <Alert severity="info">Seleccioná facturas para previsualizar la orden de pago.</Alert>
    );
  }

  const total = included.reduce(
    (sum, invoice) => sum + parseNumber(applications[invoice.uuid] || 0),
    0,
  );
  const totalArs = included.reduce((sum, invoice) => {
    const applied = parseNumber(applications[invoice.uuid] || 0);
    const pending = Number(invoice.pending_amount || 0);
    const pendingArs = Number(invoice.pending_amount_ars || 0);
    const rate = pending > 0 ? pendingArs / pending : 1;
    return sum + (applied * rate);
  }, 0);
  const previewCurrency = internalOrders.length === 1 ? internalOrders[0].currency : 'ARS';
  const previewTotal = internalOrders.length === 1
    ? formatCurrencyAmount(total, previewCurrency)
    : formatAmount(totalArs);
  const retentionsTotal = paymentForm.pay_now
    ? (paymentForm.retentions || []).reduce((sum, row) => sum + parseNumber(row.amount || 0), 0)
    : 0;
  const checksTotal = paymentForm.pay_now
    ? (paymentForm.checks || []).reduce((sum, row) => sum + parseNumber(row.amount || 0), 0)
    : 0;
  const netPayable = Math.max(total - retentionsTotal, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {internalOrders.length > 1 && (
        <Alert severity="warning">
          Este proveedor tiene facturas seleccionadas con moneda o cotización distinta. Se van a crear {internalOrders.length} OP para respetar la validación actual.
        </Alert>
      )}
      <DrawerFormPreview
        bodySx={{
          p: 0,
          gap: 0,
          backgroundColor: 'background.paper',
        }}
      >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 140px 190px' },
          border: '1px solid',
          borderColor: 'divider',
          borderBottom: 0,
          borderRadius: '4px 4px 0 0',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <Box sx={{ p: 2, minWidth: 0 }}>
          <Box
            component="img"
            src={logoConstructora}
            alt="Constructora Colonial"
            sx={{
              display: 'block',
              width: 180,
              maxWidth: '100%',
              mb: 1,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.25 }}>
            Constructora Colonial
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            Cuentas corrientes proveedores
          </Typography>
        </Box>
        <Box
          sx={{
            borderLeft: { xs: 0, sm: '1px solid' },
            borderRight: { xs: 0, sm: '1px solid' },
            borderTop: { xs: '1px solid', sm: 0 },
            borderBottom: { xs: '1px solid', sm: 0 },
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            backgroundColor: 'background.formTables',
          }}
        >
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 0 }}>
            ORDEN
          </Typography>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 900, lineHeight: 1.15 }}>
            OP
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            de pago
          </Typography>
        </Box>
        <Box sx={{ p: 2, textAlign: { xs: 'left', sm: 'right' }, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, lineHeight: 1.2 }}>
            Comprobante
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 900, lineHeight: 1.2, mb: 1 }}>
            N° Borrador
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 800, lineHeight: 1.2 }}>
            Fecha
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            {form.emission_date || '-'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.25,
          border: '1px solid',
          borderColor: 'divider',
          p: 2,
          minWidth: 0,
        }}
      >
        <InvoicePreviewField label="Proveedor" value={group.providerName} highlight />
        <InvoicePreviewField label="Moneda" value={internalOrders.length === 1 ? previewCurrency : group.currencies.join(' / ')} />
        <InvoicePreviewField label="Facturas" value={included.length} />
        <InvoicePreviewField label="Estado" value={getPaymentOrderStatusLabel(status)} highlight />
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderTop: 0,
          borderBottom: 0,
          p: 2,
          minWidth: 0,
        }}
      >
        <GeneralTableBox
          hasMaxHeight={false}
          sx={{
            minHeight: 'auto',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <HeadTableCell>Fecha</HeadTableCell>
                <HeadTableCell>Factura</HeadTableCell>
                <HeadTableCell>Concepto</HeadTableCell>
                <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pendiente</HeadTableCell>
                <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Importe aplicado</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {included.map((invoice) => (
                <TableRow key={invoice.uuid}>
                  <RowTableCell>{invoice.invoice_date || '-'}</RowTableCell>
                  <RowTableCell sx={{ fontWeight: 800 }}>{invoice.receipt_type} {invoice.receipt_number}</RowTableCell>
                  <RowTableCell>{invoice.concept || '-'}</RowTableCell>
                  <RowTableCell align="right" sx={{ textAlign: 'right' }}>
                    {formatCurrencyAmount(invoice.pending_amount, invoice.currency)}
                  </RowTableCell>
                  <RowTableCell align="right" sx={{ textAlign: 'right', fontWeight: 800 }}>
                    {formatCurrencyAmount(applications[invoice.uuid] || 0, invoice.currency)}
                  </RowTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GeneralTableBox>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.25,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '0 0 4px 4px',
          p: 2,
          minWidth: 0,
        }}
      >
        <AmountPanel label="Total aplicado" value={previewTotal} />
        <AmountPanel label="Retenciones" value={formatCurrencyAmount(retentionsTotal, previewCurrency)} />
        <AmountPanel label="Neto a pagar" value={formatCurrencyAmount(netPayable, previewCurrency)} />
        <AmountPanel label="Cheques" value={formatCurrencyAmount(checksTotal, previewCurrency)} />
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          mt: 1.25,
          minWidth: 0,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 800, mb: 0.5 }}>
          Observaciones
        </Typography>
        <Typography variant="body2" sx={{ color: form.observations ? 'text.primary' : 'text.secondary', fontWeight: 600 }}>
          {form.observations || 'Sin observaciones'}
        </Typography>
      </Box>
      </DrawerFormPreview>
    </Box>
  );
};

export default PaymentOrderPreview;
